import {ConfigType} from 'smag-commons/cfg/connector';
import {
    DataType as ModbusDataType,
    Endianness as ModbusEndianness,
    FrameType as ModbusFrameType,
    RegType as ModbusRegType
} from 'smag-commons/cfg/connector/modbus';
import {DataType as IEC61850DataType, RefType as IEC61850RefType} from 'smag-commons/cfg/connector/iec61850';
import {DeviceMode as MergeDeviceMode} from 'smag-commons/cfg/connector/merge';
import {
    Measure as MeteoMeasure,
    MeasureType as MeteoMeasureType,
    SourceType as MeteoSourceType
} from 'smag-commons/cfg/connector/meteo';
import {
    AuthType as MqttAuthType,
    Device,
    DeviceType,
    DeviceType as MqttDeviceType
} from 'smag-commons/cfg/connector/mqtt';
import {EntitySubType, EntityType} from 'smag-commons/cfg/hierarchy';
import {mkDeviceWrapper, MqttEnergyProfile} from './mqtt';
import {Chain2GateMqttDeviceWrapper} from './mqtt/chain2gate';
import {DinoPlusMqttDeviceWrapper} from './mqtt/dinoplus';
import {DuelMqttDeviceWrapper} from './mqtt/duel';
import {MiniGatewayMqttDeviceWrapper} from './mqtt/miniGateway';
import {UrbanDatasetMqttDeviceWrapper} from './mqtt/urbanDataset';

export const CONNECTABLE_CONNECTOR_TYPES: ConfigType[] = [
    ConfigType.MQTT
];

export const CONNECTOR_TYPES: ConfigType[] = [
    ConfigType.MODBUS,
    ConfigType.BACNET,
    ConfigType.IEC61850,
    ConfigType.MERGE,
    ConfigType.METEO
];

export const DEFAULT_POLLING_INTERVAL = 60;

export const EDITOR_BUILTIN_TYPES: string[] = [
    'None',
    'True',
    'False',
    'int',
    'float',
    'bool',
    'str',
    'list',
    'tuple',
    'set',
    'dict'
];

export const EDITOR_BUILTIN_FUNCTIONS: string[] = [
    'any',
    'all',
    'sum',
    'len',
    'range',
    'sorted',
    'reversed',
    'min',
    'max',
    'abs',
    'pow',
    'round'
];

/// MODBUS ///

export const MODBUS_FRAME_TYPES: ModbusFrameType[] = [
    ModbusFrameType.SOCKET,
    ModbusFrameType.RTU,
    ModbusFrameType.ASCII,
    ModbusFrameType.BINARY
];

export const MODBUS_DATA_TYPES: ModbusDataType[] = [
    ModbusDataType.FLOAT,
    ModbusDataType.INTEGER,
    ModbusDataType.BOOLEAN
];

export const MODBUS_REG_TYPES: ModbusRegType[] = [
    ModbusRegType.DISCRETE,
    ModbusRegType.COIL,
    ModbusRegType.INPUT,
    ModbusRegType.HOLDING
];

export const MODBUS_WORD_TYPES: ModbusEndianness[] = [
    ModbusEndianness.BIG,
    ModbusEndianness.LITTLE
];

export const MODBUS_DEFAULT_GATEWAY_PORT = 502;
export const MODBUS_DEFAULT_GATEWAY_FRAME_TYPE = ModbusFrameType.SOCKET;
export const MODBUS_DEFAULT_GATEWAY_RETRY_ON_EMPTY = false;
export const MODBUS_DEFAULT_DEVICE_ADDRESS = 1;
export const MODBUS_DEFAULT_DEVICE_DATA_TYPE = ModbusDataType.INTEGER;
export const MODBUS_DEFAULT_DEVICE_REGISTER_TYPE = ModbusRegType.INPUT;
export const MODBUS_DEFAULT_DEVICE_ENDIANNESS = ModbusEndianness.BIG;

export interface ModbusGatewayTemplate {
    frame?: ModbusFrameType;
}

export interface ModbusDeviceTemplate {
    suffix: string;
    uom?: string;
    address: number;
    register: number | number[];
    dataType: ModbusDataType;
    regType: ModbusRegType;
    word: ModbusEndianness;
    expression?: string;
}

export interface ModbusTemplate {
    entityType: EntityType;
    entitySubType: EntitySubType;
    gateway: ModbusGatewayTemplate;
    devices: ModbusDeviceTemplate[];
}

export const MODBUS_TEMPLATES: ModbusTemplate[] = [];

/// BACNET ///

export const BACNET_DEFAULT_TTL = 900;

/// IEC61850 ///

export const IEC61850_DATA_TYPES: IEC61850DataType[] = [
    IEC61850DataType.FLOAT,
    IEC61850DataType.INTEGER,
    IEC61850DataType.BOOLEAN
];

export const IEC61850_REF_TYPES: IEC61850RefType[] = [
    IEC61850RefType.MEASURE,
    IEC61850RefType.CONTROL,
    IEC61850RefType.STATUS
];

export const IEC61850_DEFAULT_HOST_PORT = 102;
export const IEC61850_DEFAULT_DEVICE_DATA_TYPE = IEC61850DataType.FLOAT;
export const IEC61850_DEFAULT_DEVICE_REF_TYPE = IEC61850RefType.MEASURE;

/// MERGE ///

export const MERGE_DEVICE_MODES: MergeDeviceMode[] = [
    MergeDeviceMode.WAIT_FOR_ALL,
    MergeDeviceMode.EARLY_EMIT
];

export const MERGE_DEFAULT_DEVICE_MODE = MergeDeviceMode.WAIT_FOR_ALL;

/// METEO ///

export const METEO_SRC_TYPES: MeteoSourceType[] = [
    MeteoSourceType.METEOMATICS
];

export const METEO_MEASURE_TYPES: MeteoMeasureType[] = [
    MeteoMeasureType.TEMPERATURE,
    MeteoMeasureType.RADIATION,
    MeteoMeasureType.DEW_POINT,
    MeteoMeasureType.WIND_CHILL,
    MeteoMeasureType.HEAT_INDEX,
    MeteoMeasureType.PRESSURE,
    MeteoMeasureType.HUMIDITY,
    MeteoMeasureType.RAINFALL,
    MeteoMeasureType.UV,
    MeteoMeasureType.LIFTED_INDEX,
    MeteoMeasureType.CLOUD_COVERAGE,
    MeteoMeasureType.WIND_SPEED,
    MeteoMeasureType.WIND_DIR,
    MeteoMeasureType.WIND_GUSTS,
    MeteoMeasureType.WEATHER_STATE
];

export const METEO_DEFAULT_POLLING_CRON = '0 7,19 * * *'; // everyday @ 7am, 7pm
export const METEO_DEFAULT_POLLING_CRON_TIMEZONE = 'Europe/Rome';
export const METEO_DEFAULT_OFFSET = 0; // in hours
export const METEO_DEFAULT_DURATION = 72; // in hours
export const METEO_DEFAULT_GRANULARITY = 900; // in seconds

export const METEO_SUGGESTED_UOMS: { [srcType in MeteoSourceType]?: { [measType in MeteoMeasureType]?: string } } = {
    [MeteoSourceType.METEOMATICS]: {
        [MeteoMeasureType.TEMPERATURE]: '°C',
        [MeteoMeasureType.RADIATION]: 'W/m^2',
        [MeteoMeasureType.DEW_POINT]: '°C',
        [MeteoMeasureType.WIND_CHILL]: '°C',
        [MeteoMeasureType.HEAT_INDEX]: '°C',
        [MeteoMeasureType.PRESSURE]: 'Pa',
        [MeteoMeasureType.HUMIDITY]: '%',
        [MeteoMeasureType.RAINFALL]: 'mm',
        [MeteoMeasureType.UV]: 'idx',
        [MeteoMeasureType.LIFTED_INDEX]: 'K',
        [MeteoMeasureType.CLOUD_COVERAGE]: '%',
        [MeteoMeasureType.WIND_SPEED]: 'm/s',
        [MeteoMeasureType.WIND_DIR]: 'deg',
        [MeteoMeasureType.WIND_GUSTS]: 'm/s'
    }
};

export const METEO_DEFAULT_MEASURES: { [srcType in MeteoSourceType]?: (MeteoMeasure & { suffix: string })[] } = {
    [MeteoSourceType.METEOMATICS]: [
        {
            suffix: 'temperature',
            type: MeteoMeasureType.TEMPERATURE,
            uom: METEO_SUGGESTED_UOMS[MeteoSourceType.METEOMATICS][MeteoMeasureType.TEMPERATURE]
        },
        {
            suffix: 'radiation',
            type: MeteoMeasureType.RADIATION,
            uom: METEO_SUGGESTED_UOMS[MeteoSourceType.METEOMATICS][MeteoMeasureType.RADIATION]
        },
        {
            suffix: 'state',
            type: MeteoMeasureType.WEATHER_STATE
        }
    ]
};

/// MQTT ///

export const MQTT_AUTH_TYPES: MqttAuthType[] = [
    MqttAuthType.CREDENTIALS,
    MqttAuthType.CERTIFICATES
];

export const MQTT_DEV_TYPES: MqttDeviceType[] = [
    MqttDeviceType.CHAIN2GATE,
    MqttDeviceType.DINOPLUS_MOME,
    MqttDeviceType.DUEL
];

export const MQTT_ENERGY_PROFILES: MqttEnergyProfile[] = [
    MqttEnergyProfile.CONSUMER,
    MqttEnergyProfile.PRODUCER_PROSUMER
];

export const MQTT_DEFAULT_ENERGY_PROFILE = MqttEnergyProfile.CONSUMER;
export const MQTT_MAX_K = 200;

export function wrapMqttDevice(devId: string, device: Device) {
    switch (device?.type) {
        case DeviceType.CHAIN2GATE:
            return mkDeviceWrapper(Chain2GateMqttDeviceWrapper, devId, device);
        case DeviceType.DINOPLUS_MOME:
            return mkDeviceWrapper(DinoPlusMqttDeviceWrapper, devId, device);
        case DeviceType.DUEL:
            return mkDeviceWrapper(DuelMqttDeviceWrapper, devId, device);
        case DeviceType.MGW:
            return mkDeviceWrapper(MiniGatewayMqttDeviceWrapper, devId, device);
        case DeviceType.URBAN_DATASET:
            return mkDeviceWrapper(UrbanDatasetMqttDeviceWrapper, devId, device);
        default:
            return null;
    }
}

export const HOST_PATTERN = /^[a-zA-Z0-9./\-@#$%&*_=~:?|\\]*$/;
export const ID_PATTERN = /^[a-zA-Z0-9_\- ]*$/;
export const HOST_MAX_CHARS = 100;
export const ID_MAX_CHARS = 100;
export const DEFAULT_MAX_CHARS = 50;

